import Label from 'components/Label/Label';
import { FC, FormEvent, useEffect, useState } from 'react';
import Avatar from 'shared/Avatar/Avatar';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import Input from 'shared/Input/Input';
import CommonLayout from './CommonLayout';
import { useUser } from 'providers/UserProvider';
import { resizeProfilePic } from 'utils/imgCompressing';
import { ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from 'utils/firestore';
import { updateUser } from 'utils/getUser';
import { updateEmail } from 'firebase/auth';
import { getImgUrl } from 'utils/getImgUrl';
import { useNavigate } from 'react-router-dom';

export interface SettingPageProps {
	className?: string;
}

const SettingPage: FC<SettingPageProps> = ({ className = '' }) => {
	const { user, setUser } = useUser();

	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(true);

	const [isUpdating, setIsUpdating] = useState(false);

	const [username, setUsername] = useState('');

	const [email, setEmail] = useState('');

	const [img, setImg] = useState('./defaultImg.png');

	const updateUserInfo = async (e: FormEvent) => {
		setIsUpdating(true);
		e.preventDefault();
		let _user = user;
		_user.name = username;
		_user.email = email;
		if (typeof img !== 'string') {
			_user.img = `${_user.id}-profilePic.png`;
			const storageRef = ref(
				storage,
				`profilePics/${_user.id}/${_user.id}-profilePic.png`
			);
			await uploadBytes(storageRef, img);
		}

		await updateUser(_user);

		if (auth.currentUser && auth.currentUser.email !== email) {
			await updateEmail(auth.currentUser, _user.email);
		}

		setUser({ ...user, ..._user });
		setIsUpdating(false);
		navigate('/dashboard');
	};

	useEffect(() => {
		if (user) {
			setIsLoading(false);
			setUsername(user.name);
			setEmail(user.email);
			setImg(user.img);
		} else {
			setIsLoading(true);
		}
	}, [user]);

	const setImage = async (image: any | null) => {
		try {
			const compresImage = await resizeProfilePic(image);
			setImg(compresImage);
		} catch (err) {
			console.log(err);
		}
	};

	if (!isLoading) {
		return (
			<div className={`nc-SettingPage ${className}`} data-nc-id='SettingPage'>
				<CommonLayout>
					<div className='space-y-6 sm:space-y-8'>
						{/* HEADING */}
						<h2 className='text-3xl font-semibold'>Account infomatie</h2>
						<div className='w-14 border-b border-neutral-200 dark:border-neutral-700'></div>
						<div className='flex flex-col md:flex-row'>
							<div className='flex-shrink-0 flex items-start'>
								<div className='relative rounded-full overflow-hidden flex'>
									<Avatar imgUrl={getImgUrl(img, user)} sizeClass='w-32 h-32' />
									<div className='absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-neutral-50 cursor-pointer'>
										<svg
											width='30'
											height='30'
											viewBox='0 0 30 30'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path
												d='M17.5 5H7.5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5V20M5 20V22.5C5 23.163 5.26339 23.7989 5.73223 24.2678C6.20107 24.7366 6.83696 25 7.5 25H22.5C23.163 25 23.7989 24.7366 24.2678 24.2678C24.7366 23.7989 25 23.163 25 22.5V17.5M5 20L10.7325 14.2675C11.2013 13.7988 11.8371 13.5355 12.5 13.5355C13.1629 13.5355 13.7987 13.7988 14.2675 14.2675L17.5 17.5M25 12.5V17.5M25 17.5L23.0175 15.5175C22.5487 15.0488 21.9129 14.7855 21.25 14.7855C20.5871 14.7855 19.9513 15.0488 19.4825 15.5175L17.5 17.5M17.5 17.5L20 20M22.5 5H27.5M25 2.5V7.5M17.5 10H17.5125'
												stroke='currentColor'
												strokeWidth={1.5}
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>

										<span className='mt-1 text-xs'>Change Image</span>
									</div>
									<input
										type='file'
										className='absolute inset-0 opacity-0 cursor-pointer'
										accept='image/png, image/jpeg'
										onChange={(event: any | null) => {
											setImage(event.target.files[0]);
										}}
										disabled={isUpdating}
									/>
								</div>
							</div>
							<form
								className='flex-col flex flex-grow mt-10 md:mt-0 md:pl-16 max-w-3xl space-y-6'
								onSubmit={(e: FormEvent) => updateUserInfo(e)}
							>
								<div>
									<Label>Name</Label>
									<Input
										changeValue={setUsername}
										className='mt-1.5'
										disabled={isUpdating}
										defaultValue={username}
									/>
								</div>

								<div>
									<Label>Email</Label>
									<Input
										changeValue={setEmail}
										className='mt-1.5'
										disabled={isUpdating}
										defaultValue={email}
									/>
								</div>

								<ButtonPrimary
									disabled={isUpdating}
									loading={isUpdating}
									className='self-end max-w-max'
									type='submit'
								>
									Veranderingen opslaan
								</ButtonPrimary>
							</form>
						</div>
					</div>
				</CommonLayout>
			</div>
		);
	} else {
		return <p>Loading</p>;
	}
};

export default SettingPage;
